import Button from "@/components/Button";
import { InfoIcon, exchangeIcon } from "@/designSystemIcons";
import { formatTime } from "@/functions/formatTime";
import { MetaRatingIcon } from "@/icons";
import { cn } from "@/lib/utils";
import {
  activeSquadMetaRatingSelector,
  computeNewPlayerRecs,
  fetchRecommendations,
  myClubSelector,
  recommendationsSelector,
} from "@/redux/myClub/myClubSlice";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { userSubscriptionSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { LearnMorePopup } from "../LearnMorePopup";
import {
  EliteSubscriberRecommendationCardHook,
  EmptyRecommendationCard,
  ErrorRecommendationCard,
  RecommendationCard,
} from "./RecommendationCard";
import { ScrollContainer } from "./ScrollElements";

interface RecommendationsProps {
  importFinished: boolean;
  handleUpdateClubRecsFinished: () => void;
}

export const Recommendations = ({
  importFinished,
  handleUpdateClubRecsFinished,
}: RecommendationsProps) => {
  const recommendations = useSelector(recommendationsSelector);
  const [recommendationsAsJSX, setRecommendationsAsJSX] = useState<
    React.ReactNode[]
  >([]);
  const dispatch = useDispatch<AppDispatch>();
  const { fetchRecommendationStatus, computeRecommendationStatus, clubStats } =
    useSelector(myClubSelector);
  const activeSquadMetaRating = useSelector(activeSquadMetaRatingSelector);
  const subscription = useSelector(userSubscriptionSelector);
  const recommendationsUnlocked = subscription === "diamond";
  const [hasUpdated, setHasUpdated] = useState(false);
  const [updateStatusLabel, setUpdateStatusLabel] = useState("");
  const navigate = useNavigate();
  const [showLearnMore, setShowLearnMore] = useState(false);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const memoedLearnMorePopup = useMemo(
    () => <LearnMorePopup onClose={() => setShowLearnMore(false)} />,
    [showLearnMore],
  );
  const [recommendationDescriptionLabel, setRecommendationDescriptionLabel] =
    useState("");
  const [recommendationsError, setRecommendationsError] = useState(false);

  const noRecommendations = useMemo(() => {
    return (
      (recommendations.length === 0 &&
        fetchRecommendationStatus === "fulfilled") ||
      fetchRecommendationStatus === "rejected"
    );
  }, [recommendations, fetchRecommendationStatus]);

  const neverClubImportedBefore = useMemo(
    () => !clubStats.lastImportedAt,
    [clubStats],
  );

  const neverMadeRecsBefore = useMemo(
    () => !clubStats.squadRecsLastUpdatedAt,
    [clubStats],
  );

  const handleComputeNewPlayerRecs = async () => {
    if (recommendationsUnlocked) {
      await dispatch(computeNewPlayerRecs());
      setHasUpdated(true);
      setRecommendationsError(false);
      handleUpdateClubRecsFinished();
    }
  };

  useEffect(() => {
    if (
      neverMadeRecsBefore &&
      neverClubImportedBefore &&
      recommendationsUnlocked
    ) {
      setRecommendationDescriptionLabel(
        "Make a fresh Club Import to get new recommendations",
      );
    } else if (
      neverMadeRecsBefore &&
      !neverClubImportedBefore &&
      recommendationsUnlocked
    ) {
      handleComputeNewPlayerRecs();
      setRecommendationDescriptionLabel("");
    } else if (
      noRecommendations &&
      !neverMadeRecsBefore &&
      recommendationsUnlocked
    ) {
      setRecommendationDescriptionLabel("Couldn't find new recommendations");
      setRecommendationsError(true);
    } else {
      setRecommendationDescriptionLabel("");
      setRecommendationsError(false);
    }
  }, [
    noRecommendations,
    neverClubImportedBefore,
    neverMadeRecsBefore,
    recommendationsUnlocked,
  ]);

  useEffect(() => {
    if (importFinished && computeRecommendationStatus !== "pending") {
      handleComputeNewPlayerRecs();
    }
  }, [importFinished]);

  useEffect(() => {
    const timeSinceLastRecsUpdate =
      new Date().getTime() - clubStats.squadRecsLastUpdatedAt;
    if (!hasUpdated) {
      setUpdateStatusLabel(formatTime(timeSinceLastRecsUpdate) + " ago");
    } else {
      const numberOfNewRecommendations = recommendations.reduce((acc, rec) => {
        return acc + (rec.isNew ? 1 : 0);
      }, 0);
      if (numberOfNewRecommendations > 0) {
        setUpdateStatusLabel(
          `Updated - ${numberOfNewRecommendations} new found`,
        );
      } else {
        setUpdateStatusLabel("Updated – 0 new found");
      }
    }
  }, [
    clubStats.squadRecsLastUpdatedAt,
    clubStats.squadChangedSinceLastRecs,
    recommendations,
    isMobile,
    hasUpdated,
  ]);

  useEffect(() => {
    dispatch(fetchRecommendations());
  }, []);

  const handleClickPlayer = ({
    resourceId,
    playerRoleId,
    evoPathId,
    evolutionPath,
    canEvolve,
    playerName,
  }: {
    resourceId: number;
    playerRoleId: number;
    evoPathId?: number;
    evolutionPath?: number[];
    canEvolve?: boolean;
    playerName: string;
  }) => {
    const navLink = generateNavigationUrl({
      playerRoleId,
      resourceId,
      name: playerName,
      evolutionPath,
      evoPathId,
      canEvolve,
    });
    navigate(navLink, {
      state: {
        from: "/my-club",
        scrollPosition: window.scrollY,
      },
    });
  };

  useEffect(() => {
    const loadingRecs = () =>
      setRecommendationsAsJSX([
        Array.from({ length: 10 }).map((_, index) => (
          <EmptyRecommendationCard key={index} loading />
        )),
      ]);

    const noRecs = () => {
      let eliteSubscriberRecommendationCardHook = null;
      if (subscription === "gold") {
        eliteSubscriberRecommendationCardHook = (
          <EliteSubscriberRecommendationCardHook
            key={0}
            description="Here we will show you the top upgrades for your squad. Get full access with Ultimate."
          />
        );
      }
      setRecommendationsAsJSX([
        eliteSubscriberRecommendationCardHook,
        Array.from({ length: 9 }).map((_, index) => (
          <EmptyRecommendationCard key={index + 1} />
        )),
      ]);
    };

    if (subscription === "gold") {
      noRecs();
    } else if (
      fetchRecommendationStatus === "fulfilled" &&
      computeRecommendationStatus !== "pending"
    ) {
      recommendations.length > 0
        ? setRecommendationsAsJSX(
            recommendations.map((rec, index) => (
              <RecommendationCard
                key={index}
                recommendation={rec}
                onClickPlayer={handleClickPlayer}
              />
            )),
          )
        : noRecs();
    } else {
      loadingRecs();
    }
  }, [
    subscription,
    fetchRecommendationStatus,
    computeRecommendationStatus,
    recommendations,
  ]);

  return (
    <>
      <div className={cn("relative min-h-96")}>
        <div className="flex flex-row items-center gap-x-2 text-sm mobile:text-base ml-4 touch-device:ml-2">
          <div className="flex flex-row items-center w-full">
            <div
              className={cn(
                "flex flex-row items-center gap-x-1 font-semibold",
                !clubStats.activeSquad?.name && "invisible",
              )}
            >
              <p className="text-white">{clubStats.activeSquad?.name}</p>
              <p className="text-dark-tertiary">
                {activeSquadMetaRating?.toFixed(1)}
              </p>
              <span className="text-dark-tertiary">
                <MetaRatingIcon width="16" height="16" />
              </span>
            </div>

            <div className="ml-auto mr-4 mobile:mr-0">
              <Button
                variant={
                  !neverClubImportedBefore && recommendationsUnlocked
                    ? "primary"
                    : "disabled"
                }
                label="Update"
                icon={
                  <div
                    className={cn(
                      "w-3 h-3",
                      computeRecommendationStatus === "pending" &&
                        "animate-spin",
                    )}
                  >
                    {exchangeIcon}
                  </div>
                }
                onClick={handleComputeNewPlayerRecs}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row items-center gap-2 mb-4 rounded-lg text-sm ml-4 touch-device:ml-2 my-1">
          <div
            className={cn(
              "text-dark-tertiary font-semibold items-center gap-x-1 flex flex-row text-xs mobile:text-sm",
              fetchRecommendationStatus === "fulfilled" ? "" : "invisible",
            )}
          >
            {recommendationDescriptionLabel}
            {updateStatusLabel && clubStats.squadRecsLastUpdatedAt > 0 && (
              <p className="text-dark-disabled text-xs hidden mobile:block">
                {updateStatusLabel}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-row w-full justify-between text-dark-disabled text-xs mobile:hidden px-4 -mt-4 mb-4">
          {updateStatusLabel && clubStats.squadRecsLastUpdatedAt > 0 && (
            <p>{updateStatusLabel}</p>
          )}
        </div>
        <ScrollContainer>
          {recommendationsError ? (
            <ErrorRecommendationCard />
          ) : (
            recommendationsAsJSX
          )}
        </ScrollContainer>
        <button
          className="ml-auto text-right text-dark-tertiary hover:text-dark-secondary text-xs flex flex-row items-center gap-x-1 justify-end mt-2 pr-4 touch-device:pr-0"
          onClick={(e) => {
            e.stopPropagation();
            setShowLearnMore(true);
          }}
        >
          <span>Learn More</span>
          <InfoIcon width="12" height="12" />
        </button>
      </div>
      {showLearnMore && memoedLearnMorePopup}
    </>
  );
};
